import {
  ChangeEvent,
  forwardRef,
  LegacyRef,
  useEffect,
  useReducer,
} from 'react';
import classnames from 'classnames';

const initialState = {
  className: 'enabled',
  selected: false,
  disabled: false,
};

type actionProps = {
  type: string;
  selected?: boolean;
};

type stateProps = {
  className: string;
  disabled: boolean;
  selected: boolean;
};

type radioTypeProps = {
  disabled: boolean;
  id: string;
  name: string;
  selected: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  value: string;
};

const radioReducer = (state: stateProps, action: actionProps) => {
  switch (action.type) {
    case 'selected':
      return {
        ...state,
        className: action.selected ? 'selected' : 'enabled',
        selected: !!action.selected,
      };
    case 'disabled':
      return {
        ...state,
        className: 'disabled',
        disabled: true,
      };
    default:
      throw new Error(`Unhandled action: ${action.type}`);
  }
};

export default forwardRef(function Radio(
  props: radioTypeProps,
  ref: LegacyRef<HTMLInputElement>,
) {
  const { disabled, id, name, selected, ...radioProps } = props;

  const [state, dispatch] = useReducer(radioReducer, initialState);

  useEffect(() => {
    dispatch({ type: 'selected', selected });
  }, [selected, state.selected]);

  useEffect(() => {
    if (disabled) {
      dispatch({ type: 'disabled' });
    }
  }, [disabled, state.disabled]);

  return (
    <div
      className={classnames('radio', state.className)}
      data-testid="radio-test"
    >
      <input
        checked={state.selected}
        className="hidden"
        disabled={state.disabled}
        id={id}
        name={name}
        ref={ref}
        type="radio"
        {...radioProps}
      />
      <svg
        className="hidden"
        width="6"
        height="6"
        viewBox="0 0 6 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="3" cy="3" r="3" fill="white" />
      </svg>
    </div>
  );
});
